import React, {
    createContext,
    useState,
    ReactNode,
    useContext,
    Dispatch,
    SetStateAction,
  } from "react";
  
  import {
    forgotPassword,
    googleLoginUser,
    handleToken,
    loginUser,
    notify,
    registerUser,
  } from "../api/authApi";
  
  import { AxiosResponse } from "axios";
  
  interface IUser {
    id: string;
    email: string;
    isAdmin: boolean;
  }
  
  interface AuthContextProps {
    user: IUser | null;
    login: (
      email: string,
      password: string,
    ) => Promise<AxiosResponse<any, any>>;
    register: (userData: any) => Promise<AxiosResponse<any, any>>;
    logout: () => void;
    setUser: Dispatch<SetStateAction<IUser | null>>;
    googleLogin: (email: string, token: string, name: string) => void;
    handleForgotPassword: (
      email: string,
      newpassword: string,
    ) => Promise<void>;
  }
  
  export const AuthContext = createContext<AuthContextProps | undefined>(
    undefined
  );
  
  export const AuthContextProvider: React.FC<{ children: ReactNode }> = ({
    children,
  }) => {
    const [user, setUser] = useState<IUser | null>(null);
  
    const login = async (email: string, password: string) => {
      try {
        const response = await loginUser({ email, password });
        if (response.status === 200) {
          const data = response.data;
          const { user: email_id, token } = data;
          const { userID } = handleToken(data?.token);
          localStorage.setItem("token", token);
          localStorage.setItem("email", email_id);
          localStorage.setItem("isAdmin", "false");
          const event = new CustomEvent("LOGGED_IN");
          document.dispatchEvent(event);
          setUser({ id: userID, email: email_id, isAdmin: false });
        }
        return response;
      } catch (error: any) {
        throw error;
      }
    };
  
    const googleLogin = async (email: string, token: string, name: string) => {
      try {
        const response = await googleLoginUser({ email, token, name });
        if (response.status === 200) {
          const data = response.data;
          const { user: email_id, token } = data;
          const { userID } = handleToken(data?.token);
          localStorage.setItem("token", token);
          localStorage.setItem("email", email_id);
          localStorage.setItem("isAdmin", "false");
          const event = new CustomEvent("LOGGED_IN");
          document.dispatchEvent(event);
          setUser({ id: userID, email: email_id, isAdmin: false });
        }
        return response;
      } catch (error: any) {
        throw error;
      }
    };
  
    const register = async (userData: any) => {
      try {
        const response = await registerUser(userData);
        return response;
      } catch (error) {
        throw error;
      }
    };
  
    const logout = () => {
      setUser(null);
      localStorage.clear();
      notify("Successfully Logged out.Please login again.");
    };
  
    const handleForgotPassword = async (
      email: string,
      newpassword: string,
    ) => {
      try {
        const response = await forgotPassword(email, newpassword);
        setTimeout(() => {
          notify("Password Updated");
        }, 1500);
      } catch (error) {
        console.error("Failed to update user details", error);
        notify("Failed to update user password");
      }
    };
  
    return (
      <AuthContext.Provider
        value={{ user, login, register, logout, setUser, googleLogin,handleForgotPassword }}
      >
        {children}
      </AuthContext.Provider>
    );
  };
  
  export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
      throw new Error(
        "useAuthContext must be used within an AuthContextProvider"
      );
    }
    return context;
  };
  